<template>
	<v-card v-if="visible" variant="outlined" class="similar-beans mt-4">
		<v-card-title class="text-subtitle-1 d-flex align-center py-3">
			相似生豆参考
			<span class="text-caption text-medium-emphasis ml-2">根据产地 / 处理法 / 品种 / 风味匹配</span>
			<v-spacer />
			<v-progress-circular v-if="loading" indeterminate size="18" width="2" class="mr-2" />
			<v-btn v-if="hits.length" size="small" variant="text" @click="collapsed = !collapsed">
				{{ collapsed ? '展开' : '收起' }}
			</v-btn>
		</v-card-title>
		<v-card-text v-show="!collapsed" class="pt-0">
			<v-alert v-if="!loading && !hits.length && searched" type="info" variant="tonal" density="compact">
				暂无足够相似的生豆（需至少匹配产地、处理法或品种之一）
			</v-alert>
			<div v-for="hit in hits" :key="hit.bean.id" class="similar-beans__item mb-3">
				<div class="d-flex align-center flex-wrap ga-2 mb-1">
					<strong>{{ hit.bean.name }}</strong>
					<span class="text-medium-emphasis text-caption">#{{ hit.bean.id }} · {{ hit.bean.code }}</span>
					<v-chip size="x-small" color="primary" variant="tonal">匹配 {{ hit.score }}</v-chip>
					<v-chip
						v-for="m in hit.matched"
						:key="m"
						size="x-small"
						variant="outlined"
						class="ml-0"
					>
						{{ m }}
					</v-chip>
				</div>
				<div class="d-flex flex-wrap ga-2 mb-2">
					<v-btn
						size="x-small"
						variant="tonal"
						color="primary"
						:disabled="!(hit.bean.recipe_count > 0)"
						@click="goRecipes(hit.bean.id)"
					>
						烘焙方案 {{ hit.bean.recipe_count || 0 }}
					</v-btn>
					<v-btn
						size="x-small"
						variant="tonal"
						color="secondary"
						:disabled="!(hit.bean.score_count > 0)"
						@click="goCupping(hit.bean.id)"
					>
						杯测 {{ hit.bean.score_count || 0 }}
					</v-btn>
					<v-btn size="x-small" variant="text" @click="goBean(hit.bean.id)">查看豆子</v-btn>
				</div>

				<div v-if="hit.recipes?.length" class="similar-beans__recipes mb-2">
					<div class="text-caption text-medium-emphasis mb-1">参考烘焙方案</div>
					<div
						v-for="r in hit.recipes"
						:key="r.id"
						class="similar-beans__recipe d-flex flex-wrap align-center ga-2 text-caption mb-1"
					>
						<span class="font-weight-medium">#{{ r.id }}</span>
						<roast-degree-badge v-if="r.degree" :degree="r.degree" />
						<span>{{ r.beanqty }}g</span>
						<span v-if="r.fctemp">一爆 {{ r.fctemp }}℃ / {{ r.fctime }}s</span>
						<span v-if="r.totaltime">总时 {{ r.totaltime }}s</span>
						<span v-if="r.agtron">Agtron {{ r.agtron }}/{{ r.agtron_ground }}</span>
						<span v-if="r.linked_count" class="text-medium-emphasis">关联 {{ r.linked_count }} 次</span>
						<v-btn size="x-small" variant="text" color="primary" @click="goRecipe(r.id)">打开</v-btn>
					</div>
				</div>

				<div v-if="hit.cupping_summary?.count" class="similar-beans__cupping text-caption">
					<span class="text-medium-emphasis">杯测摘要</span>
					（{{ hit.cupping_summary.count }} 次）
					总分
					<strong>{{ hit.cupping_summary.total }}</strong>
					· 干香 {{ hit.cupping_summary.aroma }}
					· 风味 {{ hit.cupping_summary.flavor }}
					· 酸质 {{ hit.cupping_summary.acidity }}
					· 口感 {{ hit.cupping_summary.body }}
					· 余韵 {{ hit.cupping_summary.aftertaste }}
				</div>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
import { roastDegreeLabel } from '../roast/roastLabels'
import RoastDegreeBadge from '../roast/DegreeBadge.vue'
import { debounce } from 'lodash-es'

export default {
	name: 'BeanSimilarPanel',
	components: { RoastDegreeBadge },
	props: {
		draft: { type: Object, default: null }
	},
	data() {
		return {
			hits: [],
			loading: false,
			searched: false,
			collapsed: false,
			seq: 0
		}
	},
	computed: {
		visible() {
			return this.hasCriteria
		},
		hasCriteria() {
			const d = this.draft || {}
			return !!(
				(d.country && String(d.country).trim()) ||
				(d.variety && String(d.variety).trim()) ||
				(d.process && String(d.process).trim()) ||
				(d.flavor_tags && d.flavor_tags.length)
			)
		},
		queryKey() {
			const d = this.draft || {}
			return JSON.stringify({
				country: d.country || '',
				variety: d.variety || '',
				process: d.process || '',
				otherprocess: d.otherprocess || '',
				flavor_tags: d.flavor_tags || [],
				kind: d.kind || '',
				moisture: d.moisture || 0,
				density: d.density || 0,
				exclude_id: d.id || 0
			})
		}
	},
	watch: {
		queryKey: {
			handler() {
				this.scheduleSearch()
			},
			immediate: true
		}
	},
	created() {
		this.scheduleSearch = debounce(this.search, 500)
	},
	beforeUnmount() {
		this.scheduleSearch?.cancel?.()
	},
	methods: {
		degreeLabel: roastDegreeLabel,
		search() {
			if (!this.hasCriteria) {
				this.hits = []
				this.searched = false
				return
			}
			const d = this.draft || {}
			const seq = ++this.seq
			this.loading = true
			this.$api.bean.findSimilar(
				{
					country: d.country || '',
					variety: d.variety || '',
					process: d.process || '',
					otherprocess: d.otherprocess || '',
					flavor_tags: d.flavor_tags || [],
					kind: d.kind || '',
					moisture: Number(d.moisture) || 0,
					density: Number(d.density) || 0,
					exclude_id: d.id || 0,
					limit: 6
				},
				(r) => {
					if (seq !== this.seq) return
					this.hits = r?.objects || []
					this.searched = true
					this.loading = false
					this.collapsed = false
				},
				() => {
					if (seq !== this.seq) return
					this.hits = []
					this.searched = true
					this.loading = false
				}
			)
		},
		goRecipes(beanId) {
			this.navigate(`/roast/recipemanage?bean_id=${beanId}`)
		},
		goCupping(beanId) {
			this.navigate(`/cupping/scoremanage?bean_id=${beanId}`)
		},
		goBean(beanId) {
			this.navigate(`/bean/manage`)
			// 列表页无直达详情；保留入口
			void beanId
		},
		goRecipe(id) {
			this.navigate(`/roast/recipemanage?ids=${id}`)
		},
		navigate(path) {
			if (typeof this.goTo === 'function') {
				this.goTo(path)
				return
			}
			if (this.$router) {
				this.$router.push(path)
				return
			}
			window.location.href = path
		}
	}
}
</script>

<style scoped>
.similar-beans__item {
	padding: 0.75rem 0;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.similar-beans__item:last-child {
	border-bottom: none;
	padding-bottom: 0;
}
.similar-beans__recipe {
	padding: 0.25rem 0.5rem;
	background: rgba(25, 118, 210, 0.04);
	border-radius: 4px;
}
</style>
